import { useState, useEffect, useContext } from 'react';
// Import Shared Utils
import { get, post } from 'shared-components/src/utils/http';
// Import Local Utils
import { isPinBlocked } from '../utils/cardStatus';
import { ProgramConfigContext } from '../contexts/ProgramConfigContext';

// Self-service PIN unlock states (BP-129 display logic):
//   A - nothing to show (feature off, or the PIN is not locked)
//   B - locked, unlock available
//   C - confirming (B with the dialog open)
//   D - just unlocked; reverts to A on the next load
//   E - locked, but already unlocked in the last 24 hours
export const PIN_UNBLOCK_STATES = {
  NONE: 'A',
  LOCKED: 'B',
  CONFIRMING: 'C',
  UNLOCKED: 'D',
  LIMIT_REACHED: 'E',
};

// What the state is read from: the card's status (E6's PIN block reports pin_block) and
// when the next self-service unlock is allowed (null when no unlock fell in the last 24
// hours). The portal never reads the fail count itself.
const lockFromAccount = acc => ({
  pinBlocked: Boolean(acc && acc.cards && acc.cards[0] && isPinBlocked(acc.cards[0].status)),
  nextAvailableAt: (acc && acc.pin_unblock_next_available_at) || null,
});

// The program flag is the cardholder-facing switch; the server re-checks it on the unlock.
// A next-available time already passed means the 24 hours are over.
const stateFromLock = (enabled, { pinBlocked, nextAvailableAt }) => {
  if (!enabled || !pinBlocked) return PIN_UNBLOCK_STATES.NONE;
  return nextAvailableAt && new Date(nextAvailableAt) > new Date()
    ? PIN_UNBLOCK_STATES.LIMIT_REACHED
    : PIN_UNBLOCK_STATES.LOCKED;
};

// Refusals that mean the page is out of date (already unlocked today, no longer locked,
// feature switched off): re-read the account instead of showing an error.
const STALE_STATUSES = [403, 409, 429];

// onUnblocked runs after a successful unlock so the page can re-read the cardholder:
// the unlock also releases E6's PIN block, so the card status changes too.
export default (account, { onUnblocked } = {}) => {
  const programConfigs = useContext(ProgramConfigContext);
  const enabled = Boolean(programConfigs && programConfigs.pin_unblock_enabled);
  const [lock, setLock] = useState(lockFromAccount(account));
  const [unlocked, setUnlocked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setLock(lockFromAccount(account));
  }, [account]);

  const refresh = async () => {
    const cardholder = await get('/api/v1/cardholder_portal/cardholder');
    const fresh = cardholder.data.accounts.find(acc => acc.id === account.id);
    setLock(lockFromAccount(fresh));
  };

  const open = () => {
    setFailed(false);
    setConfirming(true);
  };

  const cancel = () => {
    setFailed(false);
    setConfirming(false);
  };

  const confirm = async () => {
    setSubmitting(true);
    setFailed(false);
    try {
      await post(
        `/api/v1/cardholder_portal/cardholder/accounts/${account.id}/unblock_pin`,
        {
          last_four_digits: account.cards[0].last_four_digits,
          surface: 'portal',
        }
      );
      setUnlocked(true);
      setConfirming(false);
      if (onUnblocked) {
        // The unlock itself succeeded; a failed re-read must not undo the success state.
        Promise.resolve()
          .then(onUnblocked)
          .catch(() => {});
      }
    } catch (e) {
      if (STALE_STATUSES.includes(e.status)) {
        setConfirming(false);
        try {
          await refresh();
        } catch (refreshError) {
          setFailed(true);
          setConfirming(true);
        }
      } else {
        // Never raw error text: the dialog shows the failed copy with a route to support.
        setFailed(true);
      }
    }
    setSubmitting(false);
  };

  let state = stateFromLock(enabled, lock);
  if (unlocked) state = PIN_UNBLOCK_STATES.UNLOCKED;
  else if (confirming) state = PIN_UNBLOCK_STATES.CONFIRMING;

  return {
    state,
    nextAvailableAt: lock.nextAvailableAt,
    submitting,
    failed,
    open,
    cancel,
    confirm,
  };
};
